import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import { MapContainer, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import { OpenFreeMapLayer } from '@/components/Shared/OpenFreeMapLayer';
import { getObjectColors } from '@/utils/objectColors';
import { employeeTone, fmtRange, initials, objectColorKey } from '../assistantFormat';
import type { AssistantEmployee, AssistantObject, AssistantTask, SuggestionFit } from '../assistantTypes';

type LatLng = [number, number];

export interface MapRoute {
  employee: AssistantEmployee;
  /** Drawn at 25 % without start/stop markers. */
  dim?: boolean;
}

export interface MapTaskMarker {
  task: AssistantTask;
  /** Rank of a top suggestion → filled numbered circle. */
  rank?: number | null;
  /** Not suggested → hollow ring at 35 %. */
  faded?: boolean;
}

export interface MapGhost {
  fit: SuggestionFit;
  task: AssistantTask;
  employee: AssistantEmployee;
}

interface Props {
  routes: MapRoute[];
  markers: MapTaskMarker[];
  objects: Map<string, AssistantObject>;
  ghost?: MapGhost | null;
  selectedTaskId?: string | null;
  onTaskClick?: (taskId: string) => void;
  /** Click on an already planned stop (live routes only). */
  onStopClick?: (employeeId: string, planningEntryTaskId: string) => void;
  /** Stop shown as selected: enlarged, ringed, permanent tooltip. */
  selectedStopId?: string | null;
  /** Stop highlighted from outside (day line hover): enlarged plus emphasised route segment. */
  highlightedStopId?: string | null;
  /** Hover on a planned stop; `null` when the pointer leaves it. */
  onStopHover?: (planningEntryTaskId: string | null) => void;
  /** Zoom to routes and non-faded markers only; faded open tasks stay drawn but do not widen the view. */
  fitIgnoresFaded?: boolean;
  className?: string;
}

const DEFAULT_CENTER: LatLng = [51.1657, 10.4515];
// why: react-leaflet re-applies an icon whenever its identity changes; caching by markup keeps
// hover re-renders from resetting every marker on the map.
const iconCache = new Map<string, L.DivIcon>();
function divIcon(html: string, size: number, anchor: [number, number]): L.DivIcon {
  const key = `${html}|${size}|${anchor.join(',')}`;
  let icon = iconCache.get(key);
  if (!icon) {
    icon = L.divIcon({ html, className: '', iconSize: [size, size], iconAnchor: anchor });
    iconCache.set(key, icon);
  }
  return icon;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

function coordsOf(lat: number | null | undefined, lng: number | null | undefined): LatLng | null {
  return lat != null && lng != null ? [lat, lng] : null;
}

function FitBounds({ points, signature }: { points: LatLng[]; signature: string }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    // why: a zoom animation that starts while the lazily loaded MapLibre layer attaches
    // leaves its canvas mid-transform, so the base map stays blank until the next move.
    if (points.length === 1) {
      map.setView(points[0], 14, { animate: false });
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [36, 36], maxZoom: 15, animate: false });
    // why: refit only when the set of drawn points changes, not on hover.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature]);
  return null;
}

function InvalidateOnResize({ container }: { container: React.RefObject<HTMLDivElement> }) {
  const map = useMap();
  useEffect(() => {
    const el = container.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [map, container]);
  return null;
}

/** Real map for the planning assistant: routes, planned stops, open tasks and a dashed insertion preview. */
export function AssistantMap({
  routes,
  markers,
  objects,
  ghost,
  selectedTaskId,
  onTaskClick,
  onStopClick,
  selectedStopId,
  highlightedStopId,
  onStopHover,
  fitIgnoresFaded = false,
  className = '',
}: Props) {
  const { t } = useTranslation('planning');
  const containerRef = useRef<HTMLDivElement>(null);

  const objectCoords = (id: string | null | undefined): LatLng | null => {
    if (!id) return null;
    const o = objects.get(id);
    return o ? coordsOf(o.latitude, o.longitude) : null;
  };
  const taskCoords = (task: AssistantTask): LatLng | null =>
    coordsOf(task.latitude, task.longitude) ?? objectCoords(task.objectId);
  const startCoords = (e: AssistantEmployee): LatLng | null =>
    coordsOf(e.startPoint?.latitude, e.startPoint?.longitude);

  const drawnRoutes = routes.map((r) => {
    const stops = (r.employee.blocks ?? [])
      .map((b) => ({ block: b, at: objectCoords(b.objectId) }))
      .filter((s): s is { block: (typeof s)['block']; at: LatLng } => s.at != null);
    const start = startCoords(r.employee);
    const line: LatLng[] = [...(start ? [start] : []), ...stops.map((s) => s.at)];
    return { ...r, stops, start, line, tone: employeeTone(r.employee) };
  });

  const liveStopObjects = new Set(
    drawnRoutes.filter((r) => !r.dim).flatMap((r) => r.stops.map((s) => s.block.objectId ?? '')),
  );

  const perObject = new Map<string, number>();
  const drawnMarkers = markers
    .map((m) => ({ ...m, at: taskCoords(m.task) }))
    .filter((m): m is MapTaskMarker & { at: LatLng } => m.at != null)
    .map((m) => {
      const objectId = m.task.objectId ?? '';
      const n = (perObject.get(objectId) ?? 0) + 1;
      perObject.set(objectId, n);
      // Open tasks on the same object as a planned stop move up-right so the stop number stays visible;
      // several of them fan out further instead of stacking on one offset.
      const offset = liveStopObjects.has(objectId) ? n : n - 1;
      return { ...m, offset, selected: m.task.taskId === selectedTaskId };
    });

  // Objects referenced by drawn routes/markers that cannot be placed.
  const missing = new Set<string>();
  routes.forEach((r) =>
    (r.employee.blocks ?? []).forEach((b) => {
      if (b.objectId && !objectCoords(b.objectId)) missing.add(b.objectId);
    }),
  );
  markers.forEach((m) => {
    if (!taskCoords(m.task)) missing.add(m.task.objectId ?? m.task.taskId ?? '');
  });
  const missingCoordinates = missing.size;

  const boundsPoints: LatLng[] = [
    ...drawnRoutes.flatMap((r) => r.line),
    ...drawnMarkers.filter((m) => !(fitIgnoresFaded && m.faded)).map((m) => m.at),
  ];
  const signature = boundsPoints.map((p) => `${p[0].toFixed(4)},${p[1].toFixed(4)}`).sort().join('|');

  // Highlighted stop: segment previous point (start or previous stop) → stop → next stop.
  let highlight: { employeeId: string; positions: LatLng[]; tone: string } | null = null;
  if (highlightedStopId) {
    for (const r of drawnRoutes) {
      if (r.dim) continue;
      const i = r.stops.findIndex((s) => s.block.planningEntryTaskId === highlightedStopId);
      if (i < 0) continue;
      const prev = i > 0 ? r.stops[i - 1].at : r.start;
      const next = r.stops[i + 1]?.at ?? null;
      highlight = {
        employeeId: r.employee.employeeId ?? '',
        positions: [...(prev ? [prev] : []), r.stops[i].at, ...(next ? [next] : [])],
        tone: r.tone,
      };
      break;
    }
  }

  // Dashed preview prev → task → next.
  let ghostLines: { positions: LatLng[]; tone: string } | null = null;
  if (ghost) {
    const at = taskCoords(ghost.task);
    const prev = ghost.fit.prevObjectId ? objectCoords(ghost.fit.prevObjectId) : startCoords(ghost.employee);
    const next = objectCoords(ghost.fit.nextObjectId);
    if (at) {
      ghostLines = {
        positions: [...(prev ? [prev] : []), at, ...(next ? [next] : [])],
        tone: employeeTone(ghost.employee),
      };
    }
  }

  return (
    <div
      ref={containerRef}
      className={`relative z-0 isolate rounded-lg overflow-hidden border border-slate-200 bg-slate-50 ${className}`}
    >
      <MapContainer center={DEFAULT_CENTER} zoom={6} scrollWheelZoom className="h-full w-full">
        <OpenFreeMapLayer />
        <FitBounds points={boundsPoints} signature={signature} />
        <InvalidateOnResize container={containerRef} />

        {drawnRoutes.map((r) =>
          r.line.length > 1 ? (
            <Polyline
              key={`route-${r.employee.employeeId}`}
              positions={r.line}
              pathOptions={{
                color: r.tone,
                weight: r.dim ? 1.5 : 2.5,
                opacity: r.dim ? 0.25 : highlight?.employeeId === r.employee.employeeId ? 0.45 : 0.85,
                lineJoin: 'round',
              }}
            />
          ) : null,
        )}

        {highlight && highlight.positions.length > 1 && (
          <Polyline
            positions={highlight.positions}
            pathOptions={{ color: highlight.tone, weight: 4, opacity: 1, lineJoin: 'round' }}
          />
        )}

        {ghostLines && ghostLines.positions.length > 1 && (
          <Polyline
            positions={ghostLines.positions}
            pathOptions={{ color: ghostLines.tone, weight: 2.5, dashArray: '5 4', opacity: 1 }}
          />
        )}

        {drawnMarkers.map((m) => {
          const { color, soft } = getObjectColors(objectColorKey(m.task.objectId, m.task.objectName));
          const size = m.selected ? 26 : m.rank ? 22 : 14;
          const filled = !!m.rank || m.selected;
          const html =
            `<div class="rounded-full grid place-items-center text-[10px] font-bold text-white" style="width:${size}px;height:${size}px;` +
            `background:${filled ? color : 'white'};border:2px solid ${color};opacity:${m.faded && !m.selected ? 0.35 : 1};` +
            `${m.selected ? `box-shadow:0 0 0 4px ${soft};` : ''}">${m.rank ? m.rank : ''}</div>`;
          const anchor: [number, number] = [size / 2 - m.offset * 13, size / 2 + m.offset * 11];
          const label = m.task.objectShortName || m.task.objectName || '';
          return (
            <Marker
              key={`task-${m.task.taskId}`}
              position={m.at}
              icon={divIcon(html, size, anchor)}
              zIndexOffset={m.selected ? 1000 : m.rank ? 500 : 0}
              eventHandlers={onTaskClick && m.task.taskId ? { click: () => onTaskClick(m.task.taskId as string) } : {}}
            >
              <Tooltip
                key={m.selected ? 'permanent' : 'hover'}
                direction="right"
                offset={[size / 2 + 4, 0]}
                permanent={m.selected}
              >
                {m.selected || m.rank ? label : `${m.task.name ?? ''} · ${label}`}
              </Tooltip>
            </Marker>
          );
        })}

        {drawnRoutes
          .filter((r) => !r.dim)
          .map((r) => (
            <RouteMarkers
              key={`stops-${r.employee.employeeId}`}
              route={r}
              selectedStopId={selectedStopId ?? null}
              highlightedStopId={highlightedStopId ?? null}
              onStopClick={onStopClick}
              onStopHover={onStopHover}
            />
          ))}
      </MapContainer>

      {missingCoordinates > 0 && (
        <span className="absolute left-2 bottom-2 z-[500] px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] text-slate-600 tabular-nums">
          {t('assistant.map.noCoordinates', { count: missingCoordinates })}
        </span>
      )}
    </div>
  );
}

function RouteMarkers({
  route,
  selectedStopId,
  highlightedStopId,
  onStopClick,
  onStopHover,
}: {
  route: {
    employee: AssistantEmployee;
    start: LatLng | null;
    stops: { block: NonNullable<AssistantEmployee['blocks']>[number]; at: LatLng }[];
    tone: string;
  };
  selectedStopId: string | null;
  highlightedStopId: string | null;
  onStopClick?: (employeeId: string, planningEntryTaskId: string) => void;
  onStopHover?: (planningEntryTaskId: string | null) => void;
}) {
  const { t } = useTranslation('planning');
  const startHtml =
    `<div class="w-5 h-5 rounded-md grid place-items-center text-[8.5px] font-bold text-white border-2 border-white" style="background:${route.tone}">` +
    `${escapeHtml(initials(route.employee.name))}</div>`;
  return (
    <>
      {route.start && (
        <Marker position={route.start} icon={divIcon(startHtml, 20, [10, 10])} zIndexOffset={800}>
          <Tooltip direction="right" offset={[12, 0]}>
            {route.employee.startPoint?.source === 'Override'
              ? t('assistant.map.startOverride', {
                  label: route.employee.startPoint.label || t('assistant.startPoint.customStart'),
                })
              : t('assistant.map.start', { name: route.employee.name ?? '' })}
          </Tooltip>
        </Marker>
      )}
      {route.stops.map((s, i) => {
        const id = s.block.planningEntryTaskId ?? '';
        const emphasised = !!id && (id === selectedStopId || id === highlightedStopId);
        const size = emphasised ? 24 : 18;
        const ring = emphasised
          ? `box-shadow:0 0 0 4px ${getObjectColors(objectColorKey(s.block.objectId, s.block.objectName)).soft};`
          : '';
        const html =
          `<div class="rounded-full grid place-items-center ${emphasised ? 'text-[11px] font-bold' : 'text-[9.5px] font-semibold'} text-white border-2 border-white" ` +
          `style="width:${size}px;height:${size}px;background:${route.tone};${ring}">${i + 1}</div>`;
        const label = s.block.objectShortName || s.block.objectName || '';
        const employeeId = route.employee.employeeId;
        const handlers: L.LeafletEventHandlerFnMap = {};
        if (id && employeeId && onStopClick) handlers.click = () => onStopClick(employeeId, id);
        if (id && onStopHover) {
          handlers.mouseover = () => onStopHover(id);
          handlers.mouseout = () => onStopHover(null);
        }
        return (
          <Marker
            key={id || `${s.block.taskId}-${i}`}
            position={s.at}
            icon={divIcon(html, size, [size / 2, size / 2])}
            // why: open-task markers on the same object (500 / 1000) overlap the stop; the stop must win the click.
            zIndexOffset={emphasised ? 1100 : 900}
            eventHandlers={handlers}
          >
            <Tooltip
              key={emphasised ? 'permanent' : 'hover'}
              direction="right"
              offset={[size / 2 + 2, 0]}
              permanent={emphasised}
            >
              {emphasised ? `${label} · ${fmtRange(s.block.start, s.block.end)}` : label}
            </Tooltip>
          </Marker>
        );
      })}
    </>
  );
}
